"use client"
import { useState } from "react"
import { useRouter } from "next/navigation"
import { Trash2, Flag } from "lucide-react"

interface Props {
  reviewId: string
  isOwner: boolean
  isAdmin: boolean
  isProgramAdmin: boolean
}

export function DeleteReviewButton({ reviewId, isOwner, isAdmin, isProgramAdmin }: Props) {
  const router = useRouter()
  const [confirming, setConfirming] = useState(false)
  const [loading, setLoading] = useState(false)

  // Program admin mode: show flag + mailto link
  if (isProgramAdmin && !isOwner && !isAdmin) {
    return (
      <a
        href={`mailto:hello@rmar.net?subject=Review removal request&body=I would like to request removal of review ID: ${reviewId}`}
        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--foreground)] hover:text-[var(--foreground)] transition-colors"
        aria-label="Request review removal"
      >
        <Flag size={12} />
        Request removal
      </a>
    )
  }

  // Owner / Admin mode: trash icon with two-step confirmation
  async function handleDelete() {
    setLoading(true)
    const res = await fetch(`/api/reviews/${reviewId}`, { method: "DELETE" })
    setLoading(false)
    if (res.ok) {
      setConfirming(false)
      router.refresh()
    }
  }

  if (confirming) {
    return (
      <div className="inline-flex items-center gap-1.5">
        <span className="text-xs text-[var(--muted-foreground)]">Delete this review?</span>
        <button
          onClick={handleDelete}
          disabled={loading}
          className="text-xs px-2.5 py-1 rounded-full bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
        >
          {loading ? "..." : "Yes, delete"}
        </button>
        <button
          onClick={() => setConfirming(false)}
          disabled={loading}
          className="text-xs px-2.5 py-1 rounded-full border border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--foreground)] hover:text-[var(--foreground)] transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    )
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-[var(--border)] text-[var(--muted-foreground)] hover:border-red-300 hover:text-red-600 transition-colors"
      aria-label="Delete review"
    >
      <Trash2 size={12} />
    </button>
  )
}
